import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth.store'
import { useSupportAddon } from '@/hooks/useSupportAddon'

export const Billing: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { tenant } = useAuthStore()
  const tenantId = tenant?.id
  const [searchParams, setSearchParams] = useSearchParams()
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error'>('success')
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly')
  const [planChangePreview, setPlanChangePreview] = useState<{
    plan: string
    billingCycle: string
    isUpgrade: boolean
    scheduled: boolean
    effectiveAt: string | null
    amount: number | null
  } | null>(null)

  // Handle Dodo checkout return state (success / cancelled / failed)
  useEffect(() => {
    const status = searchParams.get('status')
    if (!status) return

    const cleanedParams = new URLSearchParams(searchParams)
    cleanedParams.delete('status')
    cleanedParams.delete('subscription_id')
    cleanedParams.delete('email')
    setSearchParams(cleanedParams, { replace: true })

    if (status === 'active' || status === 'success') {
      showMessage(t('billing.subscriptionSuccess'), 'success')
      refetch()
      queryClient.invalidateQueries({ queryKey: ['user-profile', tenantId] })
      queryClient.invalidateQueries({ queryKey: ['support-addon-status'] })
    } else if (status === 'cancelled' || status === 'canceled') {
      showMessage(t('billing.subscriptionCancelled'), 'error')
    } else if (status === 'failed' || status === 'failure' || status === 'declined') {
      showMessage(t('billing.subscriptionFailed'), 'error')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await api.get('/subscriptions/current')
      return response.data.data
    },
  })

  const { data: usageStats } = useQuery({
    queryKey: ['usageStats'],
    queryFn: async () => {
      const response = await api.get('/widgets/usage')
      return response.data.data
    },
  })

  const createSubscriptionMutation = useMutation({
    mutationFn: async ({ plan, billingCycle }: { plan: string; billingCycle: string }) => {
      const response = await api.post('/subscriptions/create', { plan, billingCycle })
      return response.data.data
    },
    onSuccess: (data) => {
      // Redirect to Dodo checkout
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl
      }
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('billing.checkoutError'), 'error')
    },
  })

  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      await api.post('/subscriptions/cancel')
    },
    onSuccess: () => {
      showMessage(t('billing.cancelSuccess'), 'success')
      refetch()
      setShowCancelModal(false)
      queryClient.invalidateQueries({ queryKey: ['user-profile', tenantId] })
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('billing.cancelError'), 'error')
      setShowCancelModal(false)
    },
  })

  const previewPlanChangeMutation = useMutation({
    mutationFn: async ({ plan, billingCycle }: { plan: string; billingCycle: string }) => {
      const response = await api.post('/subscriptions/change-plan/preview', { plan, billingCycle })
      return response.data.data
    },
    onSuccess: (data, variables) => {
      const summary = data?.preview?.immediate_charge?.summary
      const rawAmount = summary?.total_amount ?? summary?.settlement_amount
      setPlanChangePreview({
        plan: variables.plan,
        billingCycle: variables.billingCycle,
        isUpgrade: data.isUpgrade,
        scheduled: data.scheduled,
        effectiveAt: data.effectiveAt,
        amount: typeof rawAmount === 'number' ? rawAmount / 100 : null,
      })
    },
    onError: (error: any, variables) => {
      // No paid Dodo subscription (e.g. trial) → regular checkout flow
      if (error.response?.data?.code === 'NO_ACTIVE_SUBSCRIPTION') {
        createSubscriptionMutation.mutate(variables)
        return
      }
      const code = error.response?.data?.code
      if (code === 'ALREADY_ON_PLAN' || code === 'PENDING_PLAN_CHANGE') {
        showMessage(t(code === 'ALREADY_ON_PLAN' ? 'billing.alreadyOnPlan' : 'billing.pendingPlanChange'), 'error')
        return
      }
      showMessage(error.response?.data?.error || t('billing.planChangeError'), 'error')
    },
  })

  const changePlanMutation = useMutation({
    mutationFn: async ({ plan, billingCycle }: { plan: string; billingCycle: string }) => {
      const response = await api.post('/subscriptions/change-plan', { plan, billingCycle })
      return response.data.data
    },
    onSuccess: (data) => {
      setPlanChangePreview(null)
      if (data.scheduled) {
        showMessage(t('billing.planChangeScheduled', { date: new Date(data.effectiveAt).toLocaleDateString() }), 'success')
      } else {
        showMessage(t('billing.planChangeSuccess'), 'success')
      }
      // The plan change is confirmed by Dodo webhook; poll a couple of times
      setTimeout(() => refetch(), 3000)
      setTimeout(() => refetch(), 8000)
      refetch()
      // Force a refresh of the persisted auth store so the new plan is reflected
      // across the dashboard (Widget gating, plan badges, etc.)
      queryClient.invalidateQueries({ queryKey: ['user-profile', tenantId] })
    },
    onError: (error: any) => {
      setPlanChangePreview(null)
      const code = error.response?.data?.code
      if (code === 'ALREADY_ON_PLAN' || code === 'PENDING_PLAN_CHANGE') {
        showMessage(t(code === 'ALREADY_ON_PLAN' ? 'billing.alreadyOnPlan' : 'billing.pendingPlanChange'), 'error')
        return
      }
      showMessage(error.response?.data?.error || t('billing.planChangeError'), 'error')
    },
  })

  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg)
    setMessageType(type)
    setTimeout(() => setMessage(''), 5000)
  }

  // Support add-on state and actions
  const { status: supportAddon, active: supportAddonActive } = useSupportAddon()

  const activateSupportAddonMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/subscriptions/addons/support', { billingCycle })
      return response.data.data
    },
    onSuccess: (data) => {
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl
      }
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('billing.supportAddonError'), 'error')
    },
  })

  const cancelSupportAddonMutation = useMutation({
    mutationFn: async () => {
      await api.post('/subscriptions/addons/support/cancel')
    },
    onSuccess: () => {
      showMessage(t('billing.supportAddonCancelSuccess'), 'success')
      queryClient.invalidateQueries({ queryKey: ['support-addon-status'] })
    },
    onError: (error: any) => {
      showMessage(error.response?.data?.error || t('billing.supportAddonError'), 'error')
    },
  })

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: async () => {
      const response = await api.get('/subscriptions/invoices')
      return response.data.data || []
    },
  })

  const currentPlan = data?.subscription?.plan || 'trial'
  const pendingPlan = data?.subscription?.pending_plan || null
  const pendingChangeAt = data?.subscription?.pending_change_at || null
  const cancelsAtPeriodEnd = Boolean(data?.subscription?.cancel_at_period_end)
  const subscriptionExpiresAt = data?.subscription?.expires_at || null
  // Paid = active subscription to a real paid plan (trial rows don't count)
  const isPaid = ['starter', 'growth', 'pro', 'enterprise'].includes(currentPlan)

  const handleSelectPlan = (planName: string) => {
    const target = { plan: planName.toLowerCase(), billingCycle }
    if (isPaid) {
      // Paid tenants change plan on their existing Dodo subscription
      previewPlanChangeMutation.mutate(target)
    } else {
      // Trial tenants go through Dodo checkout
      createSubscriptionMutation.mutate(target)
    }
  }

  const actionPending =
    createSubscriptionMutation.isPending ||
    previewPlanChangeMutation.isPending ||
    changePlanMutation.isPending

  // Annual prices match the Dodo product catalog (33% off / 4 months free)
  const plans = [
    { name: 'Starter', price: 29, annualPrice: 232, features: t('billing.starterFeatures', { returnObjects: true }) as string[] },
    { name: 'Growth', price: 49, annualPrice: 392, features: t('billing.growthFeatures', { returnObjects: true }) as string[] },
    { name: 'Pro', price: 79, annualPrice: 632, features: t('billing.proFeatures', { returnObjects: true }) as string[] },
    { name: 'Enterprise', price: 199, annualPrice: 1592, features: t('billing.enterpriseFeatures', { returnObjects: true }) as string[] },
  ]

  const displayPrice = (plan: { price: number; annualPrice: number }) =>
    billingCycle === 'annual' ? Math.round(plan.annualPrice / 12) : plan.price

  const usage = data?.usage
  // Proposal usage bar: only for plans with a monthly proposal cap (e.g. growth)
  const proposalStats = usageStats?.proposals
  const proposalUsage = proposalStats && proposalStats.limit > 0 ? proposalStats : null
  const proposalPercentage = proposalUsage
    ? Math.min(100, (proposalUsage.used / proposalUsage.limit) * 100)
    : 0
  const proposalBarColor =
    proposalPercentage >= 100 ? 'bg-red-500' : proposalPercentage >= 80 ? 'bg-amber-400' : 'bg-[#006cd4]'
  // Paid = active subscription to a real paid plan (trial rows don't count)
  // Next plan up in the ladder: trial→Starter, starter→Growth, growth→Pro, pro→Enterprise
  const planLadder = ['trial', 'starter', 'growth', 'pro']
  const ladderIndex = planLadder.indexOf(currentPlan)
  const nextPlan = ladderIndex >= 0 ? plans[ladderIndex] : null

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8">{t('billing.title')}</h1>

      {message && (
        <div className={`mb-6 p-4 rounded-lg ${
          messageType === 'success'
            ? 'bg-green-500/10 border border-green-500/20 text-green-400'
            : 'bg-red-500/10 border border-red-500/20 text-red-400'
        }`}>
          {message}
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('billing.currentPlan')}</h2>
        {isLoading ? (
          <div className="text-gray-400">{t('common.loading')}</div>
        ) : (
          <div className="space-y-6">
            {/* Base subscription row */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <div className="text-2xl font-bold capitalize">
                  {currentPlan}
                  {data?.subscription?.billing_cycle && (
                    <span className="ml-2 text-base font-normal text-gray-400">
                      ({t(`billing.${data.subscription.billing_cycle}`)})
                    </span>
                  )}
                </div>
                <div className="text-gray-400 mt-1">
                  {t('billing.status')} <span className={data?.subscription?.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>{data?.subscription?.status}</span>
                </div>
                {cancelsAtPeriodEnd && subscriptionExpiresAt && (
                  <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm">
                    {t('billing.cancelsAtPeriodEnd', { date: new Date(subscriptionExpiresAt).toLocaleDateString() })}
                  </div>
                )}
                {pendingPlan && pendingChangeAt && (
                  <div className="mt-3 p-3 rounded-lg bg-[#006cd4]/10 border border-[#006cd4]/20 text-[#006cd4] text-sm">
                    {t('billing.pendingDowngrade', {
                      plan: pendingPlan,
                      date: new Date(pendingChangeAt).toLocaleDateString(),
                    })}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                {nextPlan && !cancelsAtPeriodEnd && (
                  <button
                    onClick={() => handleSelectPlan(nextPlan.name)}
                    disabled={actionPending}
                    className="px-6 py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
                  >
                    {actionPending ? t('common.loading') : t('billing.upgradePlan')}
                  </button>
                )}
                {isPaid && !cancelsAtPeriodEnd && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="px-6 py-3 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors"
                  >
                    {t('billing.cancel')}
                  </button>
                )}
              </div>
            </div>

            {/* Usage bars */}
            {usage && (
              <div>
                <div className="text-sm text-gray-400 mb-2">
                  {t('billing.conversationsUsage', { used: usage.conversations, limit: usage.limit === 'unlimited' ? '∞' : usage.limit })}
                </div>
                <div className="w-64 bg-[#1a1a25] rounded-full h-3">
                  <div
                    className="h-full bg-[#006cd4] rounded-full transition-all"
                    style={{ width: `${usage.percentage}%` }}
                  />
                </div>
              </div>
            )}
            {proposalUsage && (
              <div>
                <div className="text-sm text-gray-400 mb-2">
                  {t('billing.proposalsUsage', { used: proposalUsage.used, limit: proposalUsage.limit })}
                </div>
                <div className="w-64 bg-[#1a1a25] rounded-full h-3">
                  <div
                    className={`h-full rounded-full transition-all ${proposalBarColor}`}
                    style={{ width: `${proposalPercentage}%` }}
                  />
                </div>
                {proposalPercentage >= 80 && (
                  <div className="mt-2 text-xs text-amber-400">
                    {t('billing.proposalsNearLimit')}{' '}
                    {currentPlan === 'growth' && (
                      <button
                        onClick={() => handleSelectPlan('Pro')}
                        disabled={createSubscriptionMutation.isPending}
                        className="underline font-semibold hover:text-amber-300 disabled:opacity-50"
                      >
                        {t('billing.proposalsUpgradeCta')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Support add-on row (only when active) */}
            {supportAddonActive && (
              <div className="pt-6 border-t border-white/10">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div>
                    <div className="text-2xl font-bold">
                      {t('billing.supportAddonTitle')}
                      {supportAddon?.billingCycle && (
                        <span className="ml-2 text-base font-normal text-gray-400">
                          ({t(`billing.${supportAddon.billingCycle}`)})
                        </span>
                      )}
                    </div>
                    <div className="text-gray-400 mt-1">
                      {t('billing.status')} <span className="text-green-400">{t('billing.supportAddonActive')}</span>
                    </div>
                    {supportAddon?.cancelAtPeriodEnd && supportAddon?.expiresAt && (
                      <div className="mt-3 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-yellow-400 text-sm">
                        {t('billing.supportAddonCancelsAtPeriodEnd', { date: new Date(supportAddon.expiresAt).toLocaleDateString() })}
                      </div>
                    )}
                  </div>
                  {!supportAddon?.cancelAtPeriodEnd && (
                    <button
                      onClick={() => {
                        if (window.confirm(t('billing.supportAddonCancelConfirm'))) {
                          cancelSupportAddonMutation.mutate()
                        }
                      }}
                      disabled={cancelSupportAddonMutation.isPending}
                      className="px-6 py-3 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors disabled:opacity-50"
                    >
                      {cancelSupportAddonMutation.isPending ? t('common.loading') : t('billing.supportAddonCancel')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invoice History */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('billing.invoiceHistory')}</h2>
        {invoicesLoading ? (
          <div className="text-gray-400 text-center py-8">{t('common.loading')}</div>
        ) : invoicesData && invoicesData.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-400 border-b border-white/10">
                  <th className="pb-3 pr-4">{t('billing.invoiceNumber')}</th>
                  <th className="pb-3 pr-4">{t('billing.invoiceDate')}</th>
                  <th className="pb-3 pr-4">{t('billing.invoiceType')}</th>
                  <th className="pb-3 pr-4">{t('billing.invoiceAmount')}</th>
                  <th className="pb-3 pr-4">{t('billing.invoiceStatus')}</th>
                  <th className="pb-3"></th>
                </tr>
              </thead>
              <tbody>
                {invoicesData.map((invoice: any) => (
                  <tr key={invoice.id} className="border-b border-white/5">
                    <td className="py-3 pr-4 text-sm font-mono">{invoice.id?.slice(0, 12)}...</td>
                    <td className="py-3 pr-4 text-sm">{new Date(invoice.created_at).toLocaleDateString()}</td>
                    <td className="py-3 pr-4 text-sm capitalize">{invoice.type}</td>
                    <td className="py-3 pr-4 text-sm">${Number(invoice.amount ?? 0).toFixed(2)}</td>
                    <td className="py-3 pr-4">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        invoice.status === 'succeeded' ? 'bg-green-500/10 text-green-400' :
                        invoice.status === 'failed' ? 'bg-red-500/10 text-red-400' :
                        'bg-yellow-500/10 text-yellow-400'
                      }`}>
                        {invoice.status}
                      </span>
                    </td>
                    <td className="py-3">
                      {invoice.invoice_url && (
                        <a
                          href={invoice.invoice_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#006cd4] hover:underline text-sm"
                        >
                          {t('billing.download')}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-gray-400 text-center py-8">
            {t('billing.noInvoices')}
          </div>
        )}
      </div>

      {/* Plan Comparison */}
      <div className="flex justify-center mb-6">
        <div className="inline-flex bg-[#12121a] border border-white/10 rounded-lg p-1">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
              billingCycle === 'monthly'
                ? 'bg-[#006cd4] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t('billing.monthly')}
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-6 py-2 rounded-md text-sm font-medium transition-colors ${
              billingCycle === 'annual'
                ? 'bg-[#006cd4] text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            {t('billing.annual')}
            <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-green-500/10 text-green-400">
              {t('billing.saveBadge')}
            </span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`bg-[#12121a] border rounded-xl p-6 ${
              currentPlan === plan.name.toLowerCase()
                ? 'border-[#006cd4] ring-1 ring-[#006cd4]'
                : 'border-white/10'
            }`}
          >
            <div className="text-lg font-semibold mb-2">{plan.name}</div>
            <div className="text-3xl font-bold mb-1">
              ${displayPrice(plan)}<span className="text-lg text-gray-400">/mo</span>
            </div>
            <div className="text-xs text-gray-400 mb-3 h-4">
              {billingCycle === 'annual' && t('billing.billedAnnually', { amount: plan.annualPrice })}
            </div>
            <ul className="space-y-2 mb-6">
              {plan.features.map((feature) => (
                <li key={feature} className="flex items-center gap-2 text-sm text-gray-300">
                  <span className="text-[#006cd4]">✓</span>
                  {feature}
                </li>
              ))}
            </ul>
            {currentPlan === plan.name.toLowerCase() ? (
              <div className="w-full py-2 text-center bg-[#006cd4]/20 text-[#006cd4] rounded-lg font-medium">
                {t('billing.selected')}
              </div>
            ) : cancelsAtPeriodEnd ? null : (
              <button
                onClick={() => handleSelectPlan(plan.name)}
                disabled={actionPending}
                className="w-full py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50"
              >
                {actionPending && (createSubscriptionMutation.variables?.plan === plan.name.toLowerCase() || previewPlanChangeMutation.variables?.plan === plan.name.toLowerCase())
                  ? t('common.loading')
                  : t('billing.selectPlan')
                }
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Add-ons */}
      <h2 className="text-xl font-semibold mt-12 mb-6">{t('billing.addOns')}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          className={`relative bg-[#12121a] border rounded-xl p-6 ${
            supportAddonActive
              ? 'border-[#006cd4] ring-1 ring-[#006cd4]'
              : 'border-green-500/30'
          }`}
        >
          <span className="absolute -top-3 left-6 px-3 py-1 bg-green-500 text-black text-xs font-semibold rounded-full whitespace-nowrap border border-green-400">
            {t('billing.supportAddonNewBadge')}
          </span>
          <div className="flex items-center gap-3 mb-2 pt-2">
            <div className="text-lg font-semibold">{t('billing.supportAddonTitle')}</div>
            {supportAddonActive && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#006cd4]/10 text-[#006cd4] border border-[#006cd4]/20">
                {t('billing.supportAddonActive')}
              </span>
            )}
          </div>
          <div className="text-3xl font-bold mb-1">
            ${billingCycle === 'annual' ? 10 : 15}
            <span className="text-lg text-gray-400">/mo</span>
          </div>
          <div className="text-xs text-gray-400 mb-3 h-4">
            {billingCycle === 'annual'
              ? t('billing.supportAddonPriceAnnual', { amount: 120, monthly: 10 })
              : t('billing.supportAddonPriceMonthly', { amount: 15 })}
          </div>
          <ul className="space-y-2 mb-6">
            {(t('billing.supportAddonFeatures', { returnObjects: true }) as string[]).map((feature) => (
              <li key={feature} className="flex items-center gap-2 text-sm text-gray-300">
                <span className="text-[#006cd4]">✓</span>
                {feature}
              </li>
            ))}
          </ul>
          {!supportAddonActive ? (
            <button
              onClick={() => activateSupportAddonMutation.mutate()}
              disabled={activateSupportAddonMutation.isPending}
              className="w-full py-2 bg-[#006cd4] text-white rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
            >
              {activateSupportAddonMutation.isPending ? t('common.loading') : t('billing.supportAddonActivate')}
            </button>
          ) : supportAddon?.cancelAtPeriodEnd ? (
            <div className="w-full py-2 text-center bg-yellow-500/10 text-yellow-400 rounded-lg text-sm">
              {t('billing.supportAddonCancelsAtPeriodEnd', { date: new Date(supportAddon.expiresAt as string).toLocaleDateString() })}
            </div>
          ) : (
            <div className="w-full py-2 text-center bg-[#006cd4]/20 text-[#006cd4] rounded-lg font-medium">
              {t('billing.selected')}
            </div>
          )}
        </div>
      </div>

      {/* Plan Change Preview Modal */}
      {planChangePreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{t('billing.planChangeTitle')}</h3>
            <p className="text-gray-300 mb-2">
              <span className="capitalize font-semibold">{planChangePreview.plan}</span>
              {' · '}
              {t(`billing.${planChangePreview.billingCycle}`)}
            </p>
            {planChangePreview.isUpgrade ? (
              <p className="text-gray-300 mb-6">
                {t('billing.planChangeUpgrade', {
                  amount: planChangePreview.amount !== null ? `$${planChangePreview.amount.toFixed(2)}` : '—',
                })}
              </p>
            ) : (
              <p className="text-gray-300 mb-6">
                {t('billing.planChangeDowngrade', {
                  date: planChangePreview.effectiveAt
                    ? new Date(planChangePreview.effectiveAt).toLocaleDateString()
                    : '—',
                })}
              </p>
            )}
            <div className="flex gap-3">
              <button
                onClick={() => setPlanChangePreview(null)}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => changePlanMutation.mutate({ plan: planChangePreview.plan, billingCycle: planChangePreview.billingCycle })}
                disabled={changePlanMutation.isPending}
                className="flex-1 px-4 py-2 bg-[#006cd4] hover:bg-[#0052a3] text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {changePlanMutation.isPending ? t('common.loading') : t('billing.planChangeConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-red-400 mb-4">{t('billing.cancelModalTitle')}</h3>
            <p className="text-gray-300 mb-6">
              {t('billing.cancelModalDesc')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                className="flex-1 px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg transition-colors"
              >
                {t('billing.keepSubscription')}
              </button>
              <button
                onClick={() => cancelSubscriptionMutation.mutate()}
                disabled={cancelSubscriptionMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {cancelSubscriptionMutation.isPending ? t('billing.cancelling') : t('billing.cancelSubscription')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
